import { Tagged } from 'type-fest';

import { Schema } from './schema';
import { AnyObject, ImageUrl } from './shared';

export type PlatformAppLoader = 'templates' | 'instances' | 'templatesTags';

type TemplateLink = { text: string; url: string };

type TemplateAsset = { type: 'image' | 'video'; url: string };

export enum SchemaField {
  ClusterName = 'clusterName',
  Checkbox = 'enabled',
  Grpc = 'grcp',
}

export type AnyOfEntry = {
  $ref?: string;
} & {
  type: Schema.Value;
};

export type AnyOf = AnyOfEntry[];

export type JSONSchema = SchemaProperties.Properties & {
  type?: string;
  properties?: Record<string, JSONSchema>;
  required?: string[];
  default?: any;
  enum?: string[];
  $defs: Record<string, JSONSchema>;
  $ref: string;
  items?: JSONSchema;
  anyOf: AnyOf;
};

export namespace SchemaValidation {
  export type Required = string[];

  export type Schema = {
    required?: Required;
    anyOf: AnyOf;
  };
}

export namespace SchemaProperties {
  export type Properties = {
    title: string;
    description?: string;
    default?: any;
    'x-title'?: string;
    'x-description'?: string;
    'x-logo-url'?: string;
    'x-meta-type'?: PlatformApp.InputMetaType;
    logoUrl?: string;
    type: {
      type: Schema.Value;
    };
  };

  export type Reference = {
    title: string;
    descripton?: string;
    $ref?: string;
    anyOf?: AnyOfEntry[];
    type: Schema.Value;
  };

  export type Model = Properties | Reference;
}

export namespace SchemaDefinition {
  export type DefinitionName = Tagged<'SchemDefinitionName', string>;
  export type ComponentName = Tagged<'SchemaDefinitionComponentName', string>;

  export type Model = SchemaValidation.Schema & {
    title: string;
    properties: Record<ComponentName, SchemaProperties.Model>;
  };

  export type Definitions = Record<DefinitionName, Model>;
}

export namespace PlatformApp {
  export type InputTypeParams = Record<string, string | number>;

  export enum InputMetaType {
    Integration = 'integration',
    Inline = 'inline',
    Dynamic = 'dynamic',
  }

  export type InputType = JSONSchema;

  export type InputMetadata = {
    title: string;
    description?: string;
    logoUrl?: string;
    metaType?: InputMetaType;
  };

  export type OutputType = InputType;

  export type Template = {
    name: string;
    title: string;
    shortDescription: string;
    description: string;
    version: string;
    logo: ImageUrl;
    documentationUrls: TemplateLink[];
    externalUrls: TemplateLink[];
    /**
     * todo: make it as string date format
     */
    pubDate: string;
    assets: TemplateAsset[];
    tags: string[];
    gitUrl: string;
    input: InputType;
    output: InputType;
  };

  export type Instance = PlatformAppInstance.Model;
}

export namespace PlatformAppInstance {
  export enum State {
    Healthy = 'healthy',
    Queued = 'queued',
    Progressing = 'progressing',
    Degraded = 'degraded',
    Errored = 'errored',
    Uninstalling = 'uninstalling',
    Uninstalled = 'uninstalled',
  }

  /**
   * Output URL to open app
   */
  export type Url = {
    friendlyName: string;
    path: string;
    url: string;
  };

  export type Event = {
    createdAt: string;
    state: State;
    reason: string;
    resourceEvents?: ResourceEvent[];
  };

  export type ResourceEvent = {
    kind: string;
    name: string;
    status: string;
    health?: {
      message: string;
      status: State;
    };
  };

  export type Model = {
    id: string;
    name: string;
    namespace: string;
    creator: string;
    appType: string;
    displayName: string;
    templateName: string;
    templateVersion: string;
    projectName: string;
    orgName: string;
    state: State;
    createdAt: string;
    inputParams?: Record<string, any>;
    outputParams?: Record<string, any>;
    endpoints: Url[];
  };

  export type Value = {
    instanceId: string;
    path: string;
    value: AnyObject;
  };

  export type IntegrationValue = Value & {
    title: string;
    description?: string;
    logo?: string;
    templateName: PlatformAppInstance.Model['templateName'];
    templateVersion: PlatformAppInstance.Model['templateVersion'];
    params: AnyObject;
  };
}
